import React, { useState } from "react";
import { validateEvidenceSchema } from "@sequesign/lib/schema-validation";
import type { EvidenceBlob } from "@sequesign/lib/types";

import { SCENARIO } from "../scenario/torbridge.js";
import { BUNDLES } from "../data.js";

// E. Schema violation caught at CONSTRUCTION time, live in the browser.
//
// This demonstrates the SDK's seal-time enforcement, NOT a verification
// failure. We take the real payment_instruction_created evidence, set its
// status to a value the schema does not allow, and run validateEvidenceSchema —
// the exact check the SDK's schema policy runs before sealing (see
// src/sdk/schema-policy.ts, which calls validateEvidenceSchema and throws
// SchemaValidationError on !valid). Because it fails, the SDK would refuse to
// seal, so NO receipt is ever created.
//
// We deliberately target payment_instruction_created, not policy_checked: the
// "tamper-schema" break overrides only the policy_checked schema in the shared
// virtual FS, so validating a payment blob always resolves the genuine schema
// from the registry. That keeps this refusal deterministic (the status-enum
// violation, never a stray hash mismatch) and read-only on shared state — it
// registers/clears nothing, so it cannot disturb an in-flight verification.
function makeInvalidPaymentBlob(): EvidenceBlob {
  const bundle = BUNDLES["L2-schema"];
  const key = Object.keys(bundle).find((k) => k.includes("payment-instruction"));
  const blob = JSON.parse(bundle[key!]) as EvidenceBlob;
  const content = { ...(blob.content as Record<string, unknown>) };
  // Out of the declared enum ["simulated","created","submitted","failed"].
  content.status = "definitely_maybe";
  return { ...blob, content };
}

export function SchemaRefusalDemo() {
  const copy = SCENARIO.copy.schemaRefusal;
  const [errors, setErrors] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function tryRecord() {
    setBusy(true);
    try {
      const result = await validateEvidenceSchema(makeInvalidPaymentBlob());
      setErrors(result.valid ? [] : result.errors);
    } catch (err) {
      setErrors([err instanceof Error ? err.message : String(err)]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="refusal-card">
      <div className="artifact-explainer">
        {copy.explainer.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
      <button className="refusal-btn" onClick={tryRecord} disabled={busy}>
        {busy ? "Validating…" : copy.buttonLabel}
      </button>

      {errors !== null && errors.length > 0 && (
        <div className="refusal-panel">
          <div className="refusal-panel-head">
            <span className="refusal-mark" aria-hidden="true">
              ✕
            </span>
            <strong>{copy.title}</strong>
          </div>
          <ul className="refusal-errors">
            {errors.map((e, i) => (
              <li key={i}>
                <code>{e}</code>
              </li>
            ))}
          </ul>
          <div className="refusal-note">{copy.noReceiptNote}</div>
        </div>
      )}
    </section>
  );
}
